import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ReviewStatus, ModerationStatus, ReviewFilters as IReviewFilters } from '@/types';
import { Search, Filter, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ReviewFiltersProps {
  filters: IReviewFilters;
  onFiltersChange: (filters: IReviewFilters) => void;
}

export function ReviewFilters({ filters, onFiltersChange }: ReviewFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const debouncedSearch = useCallback(
    (value: string) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onFiltersChange({ ...filters, search: value || undefined });
      }, 500);
    },
    [filters, onFiltersChange]
  );

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    if (value.length >= 3 || value.length === 0) {
      debouncedSearch(value);
    }
  };

  const handleStatusChange = (status: string) => {
    const currentStatus = filters.status || [];
    const newStatus = currentStatus.includes(status as ReviewStatus)
      ? currentStatus.filter((s) => s !== status)
      : [...currentStatus, status as ReviewStatus];
    onFiltersChange({ ...filters, status: newStatus.length ? newStatus : undefined });
  };

  const handleModerationStatusChange = (status: string) => {
    const currentStatus = filters.moderationStatus || [];
    const newStatus = currentStatus.includes(status as ModerationStatus)
      ? currentStatus.filter((s) => s !== status)
      : [...currentStatus, status as ModerationStatus];
    onFiltersChange({
      ...filters,
      moderationStatus: newStatus.length ? newStatus : undefined,
    });
  };

  const handleRatingChange = (rating: string) => {
    const ratingNum = parseInt(rating);
    const currentRatings = filters.rating || [];
    const newRatings = currentRatings.includes(ratingNum)
      ? currentRatings.filter((r) => r !== ratingNum)
      : [...currentRatings, ratingNum];
    onFiltersChange({
      ...filters,
      rating: newRatings.length ? newRatings : undefined,
    });
  };

  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    onFiltersChange({ ...filters, [field]: value || undefined });
  };

  const clearFilters = () => {
    setSearchValue('');
    onFiltersChange({});
  };

  const activeFilterCount = [
    filters.status?.length || 0,
    filters.moderationStatus?.length || 0,
    filters.rating?.length || 0,
    filters.dateFrom ? 1 : 0,
    filters.dateTo ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const hasActiveFilters = activeFilterCount > 0 || !!filters.search;

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search reviews by product, user, or content... (min 3 chars)"
            value={searchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cn(hasActiveFilters && 'border-primary')}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
              {activeFilterCount}
            </span>
          )}
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="icon" onClick={clearFilters}>
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border rounded-lg bg-muted/50">
          <div>
            <label className="text-sm font-medium mb-2 block">Review Status</label>
            <div className="space-y-2">
              {Object.values(ReviewStatus).map((status) => (
                <label key={status} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.status?.includes(status) || false}
                    onChange={() => handleStatusChange(status)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">{status}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Moderation Status</label>
            <div className="space-y-2">
              {Object.values(ModerationStatus).map((status) => (
                <label key={status} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.moderationStatus?.includes(status) || false}
                    onChange={() => handleModerationStatusChange(status)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">{status}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Rating</label>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((rating) => (
                <label key={rating} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.rating?.includes(rating) || false}
                    onChange={() => handleRatingChange(rating.toString())}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">
                    {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Date Range</label>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">From</label>
                <Input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleDateChange('dateFrom', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">To</label>
                <Input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleDateChange('dateTo', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
